/* ==========================================================================
   ORGANIC NETRA — sections.js

   Homepage and page-level interactions: hero gallery, horizontal rails,
   concern colour reveal, UGC reels, story toggle, product page variants,
   collection sorting.

   Behaviour is carried over from the approved build unchanged. Helpers
   come from main.js via window.ONTheme.
   ========================================================================== */
(function () {
  "use strict";

  var T = window.ONTheme;
  if (!T) return;

  var qs = T.qs, qsa = T.qsa, toast = T.toast, observeReveals = T.observeReveals;

  /* ====================================================== HERO GALLERY === */
  function initHero() {
    var viewport = qs("[data-hero]");
    if (!viewport) return;
    var track = qs("[data-hero-track]", viewport);
    var dotsWrap = qs("[data-hero-dots]");
    var slides = qsa(".hero__slide", track);
    if (!slides.length) return;

    /* Clone first and last for a seamless loop. */
    if (slides.length > 1) {
      var firstClone = slides[0].cloneNode(true);
      var lastClone = slides[slides.length - 1].cloneNode(true);
      [firstClone, lastClone].forEach(function (c) {
        c.setAttribute("aria-hidden", "true");
        c.removeAttribute("aria-label");
        var img = c.querySelector("img");
        if (img) img.alt = "";
      });
      track.insertBefore(lastClone, track.firstChild);
      track.appendChild(firstClone);
    }

    var all = qsa(".hero__slide", track);
    var count = slides.length;
    var index = count > 1 ? 1 : 0;
    var animating = false;

    function realIndex() {
      if (index === 0) return count - 1;
      if (index === count + 1) return 0;
      return count > 1 ? index - 1 : 0;
    }

    function apply(withTransition) {
      track.style.transition = withTransition ? "transform .62s cubic-bezier(.22,.61,.36,1)" : "none";
      track.style.transform = "translate3d(" + -index * 100 + "%,0,0)";
      all.forEach(function (n, i) { n.classList.toggle("is-active", i === index); });
      qsa(".dot", dotsWrap).forEach(function (d, i) {
        d.setAttribute("aria-selected", i === realIndex() ? "true" : "false");
      });
    }

    function go(next) {
      if (animating || count < 2) return;
      animating = true;
      index = next;
      apply(true);
    }

    track.addEventListener("transitionend", function () {
      animating = false;
      if (index === 0) { index = count; apply(false); }
      else if (index === count + 1) { index = 1; apply(false); }
    });

    if (dotsWrap) {
      slides.forEach(function (s, i) {
        var b = document.createElement("button");
        b.className = "dot";
        b.type = "button";
        b.setAttribute("role", "tab");
        b.setAttribute("aria-selected", i === 0 ? "true" : "false");
        b.setAttribute("aria-label", "Slide " + (i + 1));
        b.addEventListener("click", function () { go(i + 1); restart(); });
        dotsWrap.appendChild(b);
      });
    }

    var prev = qs("[data-hero-prev]", viewport);
    var next = qs("[data-hero-next]", viewport);
    if (prev) prev.addEventListener("click", function () { go(index - 1); restart(); });
    if (next) next.addEventListener("click", function () { go(index + 1); restart(); });

    /* Autoplay: paused on hover, focus, hidden tab and off-screen. */
    var interval = parseInt(viewport.dataset.autoplay, 10) || 6000;
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var timer = null;
    function start() { if (!reduce && !timer && count > 1) timer = setInterval(function () { go(index + 1); }, interval); }
    function stop() { clearInterval(timer); timer = null; }
    function restart() { stop(); start(); }

    viewport.addEventListener("mouseenter", stop);
    viewport.addEventListener("mouseleave", start);
    viewport.addEventListener("focusin", stop);
    viewport.addEventListener("focusout", start);
    document.addEventListener("visibilitychange", function () { document.hidden ? stop() : start(); });
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? start() : stop();
      }, { threshold: 0.2 }).observe(viewport);
    } else { start(); }

    /* Drag and swipe */
    var down = false, startX = 0, dx = 0;
    viewport.addEventListener("pointerdown", function (e) {
      if (animating || count < 2) return;
      down = true; dx = 0; startX = e.clientX;
      viewport.classList.add("is-dragging");
      track.style.transition = "none";
      stop();
    });
    viewport.addEventListener("pointermove", function (e) {
      if (!down) return;
      dx = e.clientX - startX;
      track.style.transform = "translate3d(calc(" + -index * 100 + "% + " + dx + "px),0,0)";
    });
    function endDrag() {
      if (!down) return;
      down = false;
      viewport.classList.remove("is-dragging");
      var threshold = Math.min(120, viewport.clientWidth * 0.16);
      if (dx > threshold) go(index - 1);
      else if (dx < -threshold) go(index + 1);
      else apply(true);
      start();
    }
    ["pointerup", "pointercancel", "pointerleave"].forEach(function (ev) {
      viewport.addEventListener(ev, endDrag);
    });

    viewport.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1); restart(); }
      if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1); restart(); }
    });

    qsa("img", track).forEach(function (img) {
      if (img.complete) img.classList.add("is-loaded");
      else img.addEventListener("load", function () { img.classList.add("is-loaded"); });
    });

    apply(false);
  }

  /* ============================================================== RAILS === */
  function initRails() {
    qsa("[data-rail-section]").forEach(function (section) {
      var track = qs("[data-rail]", section);
      var ctrl = qs("[data-rail-ctrl]", section);
      if (!track) return;
      var prev = ctrl ? qs("[data-rail-prev]", ctrl) : null;
      var next = ctrl ? qs("[data-rail-next]", ctrl) : null;
      var bar = qs(".rail-progress__bar", section);

      function step() {
        var first = track.firstElementChild;
        if (!first) return 320;
        var gap = parseFloat(getComputedStyle(track).columnGap || "20") || 20;
        var perView = Math.max(1, Math.floor(track.clientWidth / (first.offsetWidth + gap)));
        return (first.offsetWidth + gap) * Math.max(1, perView - (perView > 2 ? 1 : 0));
      }
      if (prev) prev.addEventListener("click", function () { track.scrollBy({ left: -step(), behavior: "smooth" }); });
      if (next) next.addEventListener("click", function () { track.scrollBy({ left: step(), behavior: "smooth" }); });

      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        if (bar) {
          var ratio = track.clientWidth / Math.max(track.scrollWidth, 1);
          bar.style.width = Math.min(100, ratio * 100) + "%";
          var travel = max > 0 ? track.scrollLeft / max : 0;
          bar.style.transform = "translateX(" + travel * ((1 / Math.max(ratio, 0.001)) - 1) * 100 + "%)";
        }
      }
      track.addEventListener("scroll", sync, { passive: true });
      window.addEventListener("resize", sync);
      setTimeout(sync, 60);

      var down = false, startX = 0, startLeft = 0, moved = 0;
      track.addEventListener("pointerdown", function (e) {
        if (e.pointerType === "touch") return;
        down = true; moved = 0;
        startX = e.clientX; startLeft = track.scrollLeft;
        track.style.scrollSnapType = "none";
      });
      track.addEventListener("pointermove", function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        moved = Math.abs(dx);
        if (moved > 4) track.scrollLeft = startLeft - dx;
      });
      ["pointerup", "pointerleave", "pointercancel"].forEach(function (ev) {
        track.addEventListener(ev, function () {
          if (!down) return;
          down = false;
          track.style.scrollSnapType = "";
        });
      });
      track.addEventListener("click", function (e) {
        if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
        moved = 0;
      }, true);

      track.addEventListener("keydown", function (e) {
        if (e.key === "ArrowRight" && next) { e.preventDefault(); next.click(); }
        if (e.key === "ArrowLeft" && prev) { e.preventDefault(); prev.click(); }
      });
    });
  }

  /* =========================================================== CONCERNS ===
     Hover reveals colour via CSS. Touch has no hover, so reveal on
     scroll-in instead. */
  function initConcerns() {
    var grid = qs("[data-concerns]");
    if (!grid || !("IntersectionObserver" in window)) return;
    if (!window.matchMedia("(hover: none)").matches) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        e.target.classList.toggle("is-revealed", e.isIntersecting && e.intersectionRatio > 0.55);
      });
    }, { threshold: [0, 0.55, 1] });
    qsa(".concern", grid).forEach(function (t) { io.observe(t); });
  }

  /* ================================================================ UGC === */
  function initReels() {
    document.addEventListener("click", function (e) {
      var play = e.target.closest("[data-reel-play]");
      if (play) {
        var media = play.closest("[data-reel]");
        var src = play.dataset.videoSrc;
        if (!media || !src) return;
        var poster = media.querySelector("img");
        var video = document.createElement("video");
        video.src = src;
        video.controls = true;
        video.playsInline = true;
        video.preload = "metadata";
        if (poster) video.poster = poster.src;
        media.innerHTML = "";
        media.appendChild(video);
        video.play();
        return;
      }

      var like = e.target.closest("[data-reel-like]");
      if (like) {
        like.setAttribute("aria-pressed", like.getAttribute("aria-pressed") === "true" ? "false" : "true");
        return;
      }

      var share = e.target.closest("[data-reel-share]");
      if (share) {
        var url = share.dataset.shareUrl;
        if (navigator.share) navigator.share({ url: url }).catch(function () {});
        else if (navigator.clipboard) navigator.clipboard.writeText(url).then(function () { toast("Link copied"); });
        else toast(url);
      }
    });
  }

  /* ============================================================= STORY === */
  function initStory() {
    var toggle = qs("[data-story-toggle]");
    if (!toggle) return;
    var panel = qs("#story-more");
    if (!panel) return;
    var arrow = toggle.querySelector("svg") ? toggle.querySelector("svg").outerHTML : "";
    toggle.addEventListener("click", function () {
      var open = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", open ? "false" : "true");
      panel.hidden = open;
      toggle.innerHTML = (open ? toggle.dataset.labelMore : toggle.dataset.labelLess) + arrow;
    });
  }

  /* ===================================================== PRODUCT PAGE === */
  function initProduct() {
    var root = qs("[data-product-root]");
    if (!root) return;

    /* Gallery */
    var main = qs("[data-gallery-main]");
    qsa("[data-gallery-thumb]").forEach(function (thumb) {
      thumb.addEventListener("click", function () {
        if (main) main.src = thumb.dataset.full;
        qsa("[data-gallery-thumb]").forEach(function (t) { t.setAttribute("aria-selected", "false"); });
        thumb.setAttribute("aria-selected", "true");
      });
    });

    /* Variants: price, MRP, discount, SKU, stock and the URL all follow. */
    var input = qs("[data-variant-input]");
    var priceEl = qs("[data-price]");
    var compareEl = qs("[data-compare]");
    var discountEl = qs("[data-discount]");
    var skuEl = qs("[data-sku]");
    var stockEl = qs("[data-stock]");
    var addBtn = qs("[data-add-button]");
    var amazonLink = qs("[data-amazon-link]");
    var addLabel = qs("[data-add-label]");
    var threshold = 10;

    qsa("[data-variant-option]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        if (btn.disabled) return;
        var d = btn.dataset;
        qsa("[data-variant-option]").forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
        btn.setAttribute("aria-pressed", "true");

        if (input) input.value = d.variantId;
        if (priceEl) priceEl.textContent = d.price;
        if (compareEl) {
          compareEl.hidden = d.hasCompare !== "true";
          compareEl.textContent = "MRP " + d.compare;
        }
        if (discountEl) {
          discountEl.hidden = d.hasCompare !== "true";
          discountEl.textContent = d.discount + "% off";
        }
        if (skuEl) skuEl.textContent = d.sku || "\u2014";

        /* Each variant has its own Amazon listing. */
        if (amazonLink) {
          if (d.amazon) { amazonLink.href = d.amazon; amazonLink.hidden = false; }
          else { amazonLink.hidden = true; }
        }

        var available = d.available === "true";
        if (stockEl) {
          stockEl.classList.toggle("pdp__stock--out", !available);
          stockEl.classList.toggle("pdp__stock--low", available && d.managed === "true" && Number(d.inventory) > 0 && Number(d.inventory) <= threshold);
          if (!available) stockEl.textContent = "Sold out";
          else if (d.managed === "true" && Number(d.inventory) > 0 && Number(d.inventory) <= threshold) {
            stockEl.textContent = "Only " + d.inventory + " left";
          } else stockEl.textContent = "In stock";
        }
        if (addBtn) addBtn.disabled = !available;
        if (addLabel) addLabel.textContent = available ? "Add to bag" : "Sold out";

        var url = new URL(window.location.href);
        url.searchParams.set("variant", d.variantId);
        history.replaceState({}, "", url);
      });
    });

    /* Accordions */
    qsa("[data-accordion]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var panel = document.getElementById(btn.getAttribute("aria-controls"));
        var open = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", open ? "false" : "true");
        if (panel) panel.hidden = open;
      });
    });
  }

  /* ========================================================= COLLECTION === */
  function initCollection() {
    var sort = qs("[data-sort]");
    if (!sort) return;
    sort.addEventListener("change", function () {
      var url = new URL(window.location.href);
      url.searchParams.set("sort_by", sort.value);
      url.searchParams.delete("page");
      window.location.href = url.toString();
    });
  }

  /* ================================================================ BOOT == */
  function init() {
    initHero();
    initRails();
    initConcerns();
    initReels();
    initStory();
    initProduct();
    initCollection();
    observeReveals();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  /* Theme Editor: re-run bindings when a section is re-rendered. */
  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:select", function () { observeReveals(); });
})();
